import React, { useEffect, useState } from 'react';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Trash2, Bell, BellOff, CheckCheck } from 'lucide-react';
import { toast } from 'sonner';

export const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/notifications');
      setNotifications(response.data);
    } catch {
      toast.error('Failed to load notifications');
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/notifications/${deleteId}`);
      toast.success('Notification deleted');
      setIsDeleteDialogOpen(false);
      setDeleteId(null);
      fetchNotifications();
    } catch {
      toast.error('Failed to delete notification');
    }
  };

  const handleMarkRead = async (notification) => {
    try {
      await api.put(`/notifications/${notification.id}`, { is_read: !notification.is_read });
      fetchNotifications();
    } catch {
      toast.error('Failed to update notification');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.patch('/notifications/mark-all-read');
      toast.success('All notifications marked as read');
      fetchNotifications();
    } catch {
      toast.error('Failed to mark all as read');
    }
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Bell className="w-6 h-6 text-sage-600" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </div>
          <div>
            <h1 className="text-2xl font-heading font-bold text-sage-700">Notifications</h1>
            <p className="text-sm text-gray-500">
              {unreadCount > 0
                ? `${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}`
                : 'All caught up'}
            </p>
          </div>
        </div>

        {unreadCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleMarkAllRead}
            className="gap-2"
          >
            <CheckCheck className="w-4 h-4" />
            Mark all read
          </Button>
        )}
      </div>

      {/* Notifications list */}
      {notifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400 gap-3">
          <BellOff className="w-12 h-12" />
          <p className="text-lg font-medium">No notifications</p>
        </div>
      ) : (
        <Accordion type="multiple" className="space-y-3">
          {notifications.map((notification) => (
            <AccordionItem
              key={notification.id}
              value={notification.id}
              className={`rounded-xl border px-4 transition-colors ${
                notification.is_read
                  ? 'border-gray-200 bg-white'
                  : 'border-sage-300 bg-sage-50'
              }`}
            >
              <AccordionTrigger className="hover:no-underline py-4">
                <div className="flex items-center gap-3 flex-1 min-w-0 pr-2">
                  {!notification.is_read && (
                    <span className="w-2 h-2 rounded-full bg-sage-500 shrink-0" />
                  )}
                  <span
                    className={`font-semibold text-sm text-left truncate ${
                      notification.is_read ? 'text-gray-600' : 'text-sage-800'
                    }`}
                  >
                    {notification.title}
                  </span>
                  {!notification.is_read && (
                    <Badge className="bg-sage-100 text-sage-700 border-sage-200 shrink-0 text-xs">
                      New
                    </Badge>
                  )}
                  <span className="ml-auto shrink-0 text-xs text-gray-400 pr-2">
                    {formatDate(notification.created_at)}
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="pb-3">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {notification.content}
                  </p>
                  <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-100">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleMarkRead(notification)}
                      className="gap-1.5 text-xs text-gray-500 hover:text-sage-700"
                    >
                      <CheckCheck className="w-3.5 h-3.5" />
                      {notification.is_read ? 'Mark unread' : 'Mark read'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setDeleteId(notification.id);
                        setIsDeleteDialogOpen(true);
                      }}
                      className="gap-1.5 text-xs text-red-400 hover:text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Delete
                    </Button>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}

      {/* Delete confirm dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Notification</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this notification? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteId(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
